import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container, Row, Col, Card, CardBody, CardHeader,
  Nav, NavItem, NavLink, TabContent, TabPane, Button, Form, FormGroup, Label, Input
} from 'reactstrap';
import classnames from 'classnames';
import { usePocketBase } from '../../../PocketBaseContext';
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CountUp from "react-countup";
import FeatherIcon from "feather-icons-react";
import DOMPurify from 'dompurify';
import Select from 'react-select';  // React-select ekledik

const ReformProductDetail = () => {
  document.title = "Products | Reform Panel";
  const { id } = useParams();
  const pb = usePocketBase();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customActiveTab, setCustomActiveTab] = useState('1');
  const [isEditing, setIsEditing] = useState(false);
  const [editedProduct, setEditedProduct] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProduct();
    fetchSuppliers();
    fetchCategories();
  }, [id, pb]);

  const fetchProduct = async () => {
    try {
      const record = await pb.collection('products').getOne(id, {
        expand: 'supplier,category_relation',
      });
      setProduct(record);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Ürün yüklenirken bir hata oluştu.');
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const records = await pb.collection('supplier').getFullList({
        sort: 'supplier_name',
      });
      setSuppliers(records.map(supplier => ({ value: supplier.id, label: supplier.supplier_name })));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const records = await pb.collection('category').getFullList({
        sort: 'category_type',
      });
      setCategories(records.map(category => ({ value: category.id, label: category.category_type })));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const sanitizeHTML = (html) => {
    return {
      __html: DOMPurify.sanitize(html)
    }
  }

  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setCustomActiveTab(tab);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedProduct({ ...product });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedProduct(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedProduct(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    if (name === 'category_relation') {
      setEditedProduct(prev => ({ ...prev, [name]: selectedOption.map(option => option.value) }));
    } else {
      setEditedProduct(prev => ({ ...prev, [name]: selectedOption.value }));
    }
  };

  const handleSaveEdit = async () => {
    try {
      const updatedProduct = await pb.collection('products').update(id, editedProduct);
      setProduct(updatedProduct);
      setIsEditing(false);
      setEditedProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
      setError('Ürün güncellenirken bir hata oluştu.');
    }
  };

  if (loading) return <div>Yükleniyor...</div>;
  if (error) return <div>{error}</div>;
  if (!product) return <div>Ürün bulunamadı.</div>;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={product.product_name} pageTitle="Ürünler" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                {isEditing ? (
                  <Form>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="product_name">Ürün Adı</Label>
                          <Input
                            type="text"
                            name="product_name"
                            id="product_name"
                            value={editedProduct.product_name}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="product_code">Ürün Kodu</Label>
                          <Input
                            type="text"
                            name="product_code"
                            id="product_code"
                            value={editedProduct.product_code}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="stock_quantity">Stok Miktarı</Label>
                          <Input
                            type="number"
                            name="stock_quantity"
                            id="stock_quantity"
                            value={editedProduct.stock_quantity}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="kdv_vat_rate">KDV Oranı</Label>
                          <Input
                            type="number"
                            name="kdv_vat_rate"
                            id="kdv_vat_rate"
                            value={editedProduct.kdv_vat_rate}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="currency">Para Birimi</Label>
                          <Input
                            type="select"
                            name="currency"
                            id="currency"
                            value={editedProduct.currency}
                            onChange={handleInputChange}
                          >
                            <option value="TRY">TRY</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="purchase_price_excl_vat">Alış Fiyatı (KDV Hariç)</Label>
                          <Input
                            type="number"
                            name="purchase_price_excl_vat"
                            id="purchase_price_excl_vat"
                            value={editedProduct.purchase_price_excl_vat}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="purchase_price_incl_vat">Alış Fiyatı (KDV Dahil)</Label>
                          <Input
                            type="number"
                            name="purchase_price_incl_vat"
                            id="purchase_price_incl_vat"
                            value={editedProduct.purchase_price_incl_vat}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="sales_price_excl_vat">Satış Fiyatı (KDV Hariç)</Label>
                          <Input
                            type="number"
                            name="sales_price_excl_vat"
                            id="sales_price_excl_vat"
                            value={editedProduct.sales_price_excl_vat}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="sales_price_incl_vat">Satış Fiyatı (KDV Dahil)</Label>
                          <Input
                            type="number"
                            name="sales_price_incl_vat"
                            id="sales_price_incl_vat"
                            value={editedProduct.sales_price_incl_vat}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="supplier">Tedarikçi</Label>
                          <Select
                            name="supplier"
                            options={suppliers}
                            value={suppliers.find(option => option.value === editedProduct.supplier)}
                            onChange={(selectedOption) => handleSelectChange(selectedOption, { name: 'supplier' })}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="category_relation">Kategori</Label>
                          <Select
                            name="category_relation"
                            options={categories}
                            isMulti
                            value={categories.filter(option => editedProduct.category_relation.includes(option.value))}
                            onChange={(selectedOptions) => handleSelectChange(selectedOptions, { name: 'category_relation' })}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <FormGroup>
                      <Label for="description">Açıklama</Label>
                      <Input
                        type="textarea"
                        name="description"
                        id="description"
                        value={editedProduct.description}
                        onChange={handleInputChange}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label for="additional_information">Ek Bilgiler</Label>
                      <Input
                        type="textarea"
                        name="additional_information"
                        id="additional_information"
                        value={editedProduct.additional_information}
                        onChange={handleInputChange}
                      />
                    </FormGroup>
                    <Button color="primary" onClick={handleSaveEdit}>Kaydet</Button>
                    <Button color="secondary" onClick={handleCancelEdit} className="ms-2">İptal</Button>
                  </Form>
                ) : (
                  <Row>
                    <Col xl={6} className="d-flex justify-content-center">
                      <div className="product-img-slider sticky-side-div">
                        <img 
                          src={pb.getFileUrl(product, product.picture)} 
                          alt={product.product_name}
                          className="img-fluid d-block mx-auto"
                        />
                      </div>
                    </Col>
                    <Col xl={6}>
                      <div className="mt-xl-0 mt-5">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <h4>{product.product_name}</h4>
                            <div className="hstack gap-3 flex-wrap">
                              <div><Link className="text-primary d-block">Tedarikçi: {product.expand?.supplier?.supplier_name}</Link></div>
                              <div className="vr"></div>
                              <div className="text-muted">Ürün Kodu : <span className="text-body fw-medium">{product.product_code}</span></div>
                              <div className="text-muted">Kategori:</div>
                              <Link className="link-danger">{product.expand?.category_relation?.map(cat => cat.category_type).join(', ')}</Link>
                              {/* <span className="text-body fw-medium">{product.expand?.category_relation?.map(cat => cat.category_type).join(', ')}</span> */}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
                          <div className="text-muted fs-16">Satış Fiyatı :</div>
                          <div className="fs-5 fw-bold">{product.sales_price_incl_vat} {product.currency}</div>
                          <div className="fs-6 text-muted">(KDV Dahil)</div>
                        </div>

                        <Row className="mt-4">
                          <Col sm={6}>
                            <div className="p-2 border border-dashed rounded">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-2">
                                  <div className="avatar-title rounded bg-transparent text-primary fs-24">
                                    <i className="ri-money-dollar-circle-fill"></i>
                                  </div>
                                </div>
                                <div className="flex-grow-1">
                                  <p className="text-muted mb-1">Alış Fiyatı :</p>
                                  <h5 className="mb-0">{product.purchase_price_incl_vat} {product.currency}</h5>
                                </div>
                              </div>
                            </div>
                          </Col>
                          <Col sm={6}>
                            <div className="p-2 border border-dashed rounded">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-2">
                                  <div className="avatar-title rounded bg-transparent text-primary fs-24">
                                    <i className="ri-stack-fill"></i>
                                  </div>
                                </div>
                                <div className="flex-grow-1">
                                  <p className="text-muted mb-1">Stok Miktarı :</p>
                                  <h5 className="mb-0">{product.stock_quantity}</h5>
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <div className="mt-4">
                          <h5 className="fs-14">Açıklama :</h5>
                          <div 
                            className="text-muted"
                            dangerouslySetInnerHTML={sanitizeHTML(product.description)}
                          />
                        </div>

                        <Row className="mt-4">
                          <Col sm={6}>
                            <Card className="card-animate bg-primary">
                              <CardBody>
                                <div className="d-flex justify-content-between">
                                  <div>
                                    <p className="fw-medium mb-0 text-white-50">Oluşturulma Tarihi</p>
                                    <h5 className="mt-4 ff-secondary fw-semibold text-white">
                                      {new Date(product.created).toLocaleDateString()}
                                    </h5>
                                  </div>
                                  <div>
                                    <div className="avatar-sm flex-shrink-0">
                                      <span className="avatar-title rounded-circle fs-2 bg-soft-light text-white">
                                        <FeatherIcon
                                          icon="calendar"
                                          className="text-white"
                                        />
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                          <Col sm={6}>
                            <Card className="card-animate bg-secondary">
                              <CardBody>
                                <div className="d-flex justify-content-between">
                                  <div>
                                    <p className="fw-medium mb-0 text-white-50">KDV Oranı</p>
                                    <h5 className="mt-4 ff-secondary fw-semibold text-white">
                                      {product.kdv_vat_rate}%
                                    </h5>
                                  </div>
                                  <div>
                                    <div className="avatar-sm flex-shrink-0">
                                      <span className="avatar-title rounded-circle fs-2 bg-soft-light text-white">
                                        <FeatherIcon
                                          icon="percent"
                                          className="text-white"
                                        />
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                        <div className="mt-4">
                          <Button color="primary" onClick={handleEditClick}>Düzenle</Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xxl={12}>
            <h5 className="mb-3">Ürün Detayları</h5>
            <Card>
              <CardBody><Row>
                  <Col lg={3}>
                    <Nav pills className="nav nav-pills flex-column nav-pills-tab custom-verti-nav-pills text-center">
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({
                            "mb-2": true,
                            active: customActiveTab === "1",
                          })}
                          onClick={() => {
                            toggleCustom("1");
                          }}
                          id="custom-v-pills-home-tab"
                        >
                          <i className="ri-file-list-line d-block fs-20 mb-1"></i>
                          Ek Bilgiler
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({
                            "mb-2": true,
                            active: customActiveTab === "2",
                          })}
                          onClick={() => {
                            toggleCustom("2");
                          }}
                          id="custom-v-pills-profile-tab"
                        >
                          <i className="ri-money-dollar-box-line d-block fs-20 mb-1"></i>
                          Fiyatlandırma Detayları
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </Col>
                  <Col lg={9}>
                    <TabContent activeTab={customActiveTab} className="text-muted mt-3 mt-lg-0">
                      <TabPane tabId="1">
                        <div className="table-responsive">
                          <table className="table mb-0">
                            <tbody>
                              <tr>
                                <th scope="row">Tedarikçi</th>
                                <td>{product.expand?.supplier?.supplier_name}</td>
                              </tr>
                              <tr>
                                <th scope="row">Ek Bilgiler</th>
                                <td>{product.additional_information}</td>
                              </tr>
                              <tr>
                                <th scope="row">Son Güncelleme</th>
                                <td>{new Date(product.updated).toLocaleString()}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </TabPane>
                      <TabPane tabId="2">
                        <div className="table-responsive">
                          <table className="table table-bordered mb-0">
                            <tbody>
                              <tr>
                                <td>Alış Fiyatı (KDV Hariç)</td>
                                <td>{product.purchase_price_excl_vat} {product.currency}</td>
                              </tr>
                              <tr>
                                <td>Alış Fiyatı (KDV Dahil)</td>
                                <td>{product.purchase_price_incl_vat} {product.currency}</td>
                              </tr>
                              <tr>
                                <td>Satış Fiyatı (KDV Hariç)</td>
                                <td>{product.sales_price_excl_vat} {product.currency}</td>
                              </tr>
                              <tr>
                                <td>Satış Fiyatı (KDV Dahil)</td>
                                <td>{product.sales_price_incl_vat} {product.currency}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </TabPane>
                    </TabContent>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ReformProductDetail;